import { Grid } from '../core/Grid.js';
import { ScoreBreakdown } from '../core/Scoring.js';
import { GameType } from '../persistence/types.js';
import { RenderConfig } from '../rendering/Renderer.js';
import type { ScoringHighlight } from './scoringRules.js';

import { MirrorsRenderer } from './mirrors/MirrorsRenderer.js';
import { MirrorsInput } from './mirrors/MirrorsInput.js';
import { MirrorsVortex } from './mirrors/MirrorsVortex.js';
import { KnightVortex } from './knight/KnightVortex.js';
import { KnightRenderer } from './knight/KnightRenderer.js';
import { KnightInput } from './knight/KnightInput.js';
import { WallsVortex } from './walls/WallsVortex.js';
import { WallsRenderer } from './walls/WallsRenderer.js';
import { WallsInput } from './walls/WallsInput.js';
import { SplittingVortex } from './splitting/SplittingVortex.js';
import { SplittingRenderer } from './splitting/SplittingRenderer.js';
import { SplittingInput } from './splitting/SplittingInput.js';
import { LineVortex } from './line/LineVortex.js';
import { LineRenderer } from './line/LineRenderer.js';
import { LineInput } from './line/LineInput.js';
import { AntennasVortex } from './antennas/AntennasVortex.js';
import { AntennasRenderer } from './antennas/AntennasRenderer.js';
import { AntennasInput } from './antennas/AntennasInput.js';
import { JumpsVortex } from './jumps/JumpsVortex.js';
import { JumpsRenderer } from './jumps/JumpsRenderer.js';
import { JumpsInput } from './jumps/JumpsInput.js';
import { IgnitionVortex } from './ignition/IgnitionVortex.js';
import { IgnitionRenderer } from './ignition/IgnitionRenderer.js';
import { IgnitionInput } from './ignition/IgnitionInput.js';
import { FloodVortex } from './flood/FloodVortex.js';
import { FloodRenderer } from './flood/FloodRenderer.js';
import { FloodInput } from './flood/FloodInput.js';
import { OrbitsVortex } from './orbits/OrbitsVortex.js';
import { OrbitsRenderer } from './orbits/OrbitsRenderer.js';
import { OrbitsInput } from './orbits/OrbitsInput.js';
import { BranchesVortex } from './branches/BranchesVortex.js';
import { BranchesRenderer } from './branches/BranchesRenderer.js';
import { BranchesInput } from './branches/BranchesInput.js';
import { CrossingVortex } from './crossing/CrossingVortex.js';
import { CrossingRenderer } from './crossing/CrossingRenderer.js';
import { CrossingInput } from './crossing/CrossingInput.js';
import { DelugeVortex } from './deluge/DelugeVortex.js';
import { DelugeRenderer } from './deluge/DelugeRenderer.js';
import { DelugeInput } from './deluge/DelugeInput.js';
import { TurnsVortex } from './turns/TurnsVortex.js';
import { TurnsRenderer } from './turns/TurnsRenderer.js';
import { TurnsInput } from './turns/TurnsInput.js';

export type GameStatus = 'not-started' | 'in-progress' | 'complete';

export interface IGame {
    getGrid(): Grid;
    checkWin(): boolean;
    getScore(): ScoreBreakdown | null;
    getStatus(): string;
    isStarted(): boolean;
    reset(): void;
    serialize(): string;
    deserialize(serialized: string): void;
    /**
     * Place a minimal fragment joining the first two digits to jumpstart a
     * solution. Returns true if anything was placed. Only ever called on an
     * empty board on a Base Grid. Games that don't implement it get no button.
     */
    autoStart?(): boolean;
    /**
     * Elements that make up the score, for the Scoring-rules overlay. Only called
     * on a won board. Games that don't implement it get the fade + rule text only.
     */
    getScoringHighlight?(): ScoringHighlight;
}

export interface IRenderer {
    render(game: IGame): void;
    getConfig(): RenderConfig;
    /** Fade the board and highlight scored elements (Scoring-rules overlay). */
    drawScoringOverlay(game: IGame, highlight: ScoringHighlight): void;
    /**
     * Turn off renderer-driven tweening, so the renderer never re-renders on its
     * own and wipes annotations drawn on top of a static scene (Scoring-rules
     * overlay, tutorial). Only implemented by renderers that self-schedule a rAF.
     */
    setSelfAnimation?(enabled: boolean): void;
}

export interface IInput {
    destroy(): void;
}

export interface GameInstance {
    game: IGame;
    renderer: IRenderer;
    input: IInput;
    render: () => void;
    reset: () => void;
    getStatus: () => string;
    checkWin: () => boolean;
    getGameStatus: () => GameStatus;
    getScore: () => ScoreBreakdown | null;
}

export interface GameDefinition {
    Game: new (grid: Grid) => IGame;
    Renderer: new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer;
    Input: new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput;
    title: string;
}

export const GAME_REGISTRY: Record<GameType, GameDefinition> = {
    mirrors: {
        Game: MirrorsVortex as unknown as new (grid: Grid) => IGame,
        Renderer: MirrorsRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: MirrorsInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Mirrors',
    },
    knight: {
        Game: KnightVortex as unknown as new (grid: Grid) => IGame,
        Renderer: KnightRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: KnightInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Knight',
    },
    walls: {
        Game: WallsVortex as unknown as new (grid: Grid) => IGame,
        Renderer: WallsRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: WallsInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Walls',
    },
    splitting: {
        Game: SplittingVortex as unknown as new (grid: Grid) => IGame,
        Renderer: SplittingRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: SplittingInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Splitting',
    },
    line: {
        Game: LineVortex as unknown as new (grid: Grid) => IGame,
        Renderer: LineRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: LineInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Line',
    },
    antennas: {
        Game: AntennasVortex as unknown as new (grid: Grid) => IGame,
        Renderer: AntennasRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: AntennasInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Antennas',
    },
    ignition: {
        Game: IgnitionVortex as unknown as new (grid: Grid) => IGame,
        Renderer: IgnitionRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: IgnitionInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Ignition',
    },
    jumps: {
        Game: JumpsVortex as unknown as new (grid: Grid) => IGame,
        Renderer: JumpsRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: JumpsInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Jumps',
    },
    flood: {
        Game: FloodVortex as unknown as new (grid: Grid) => IGame,
        Renderer: FloodRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: FloodInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Flood',
    },
    orbits: {
        Game: OrbitsVortex as unknown as new (grid: Grid) => IGame,
        Renderer: OrbitsRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: OrbitsInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Orbits',
    },
    branches: {
        Game: BranchesVortex as unknown as new (grid: Grid) => IGame,
        Renderer: BranchesRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: BranchesInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Branches',
    },
    crossing: {
        Game: CrossingVortex as unknown as new (grid: Grid) => IGame,
        Renderer: CrossingRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: CrossingInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Crossing',
    },
    deluge: {
        Game: DelugeVortex as unknown as new (grid: Grid) => IGame,
        Renderer: DelugeRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: DelugeInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Deluge',
    },
    turns: {
        Game: TurnsVortex as unknown as new (grid: Grid) => IGame,
        Renderer: TurnsRenderer as unknown as new (canvas: HTMLCanvasElement, grid: Grid) => IRenderer,
        Input: TurnsInput as unknown as new (game: IGame, renderer: IRenderer, canvas: HTMLCanvasElement, onUpdate: () => void) => IInput,
        title: 'Turns',
    },
};

export function createGameInstance(
    gameType: GameType,
    grid: Grid,
    canvas: HTMLCanvasElement,
    onUpdate: () => void
): GameInstance {
    const def = GAME_REGISTRY[gameType];
    const game = new def.Game(grid);
    const renderer = new def.Renderer(canvas, grid);
    const input = new def.Input(game, renderer, canvas, onUpdate);

    return {
        game,
        renderer,
        input,
        render: () => renderer.render(game),
        reset: () => game.reset(),
        getStatus: () => game.getStatus(),
        checkWin: () => game.checkWin(),
        getGameStatus: () => {
            if (game.checkWin()) return 'complete';
            if (game.isStarted()) return 'in-progress';
            return 'not-started';
        },
        getScore: () => game.getScore(),
    };
}

export function reactivateGameInstance(
    instance: GameInstance,
    gameType: GameType,
    canvas: HTMLCanvasElement,
    onUpdate: () => void
): void {
    const def = GAME_REGISTRY[gameType];
    instance.input = new def.Input(instance.game, instance.renderer, canvas, onUpdate);
}

export function createGameForValidation(gameType: GameType, grid: Grid): IGame {
    const def = GAME_REGISTRY[gameType];
    return new def.Game(grid);
}

export function getGameTitle(gameType: GameType): string {
    return GAME_REGISTRY[gameType].title;
}
